import { useEffect, useState } from "react";
import { Copy, Mail, ShieldAlert, ShieldCheck, ShieldOff } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { CONSENT_LABELS } from "./config";

const BADGES = {
  "": "bg-slate-100 text-slate-600",
  expresse: "bg-emerald-50 text-emerald-700",
  implicite: "bg-amber-50 text-amber-700",
  desabonne: "bg-red-50 text-red-700",
};

export default function ConsentPanel({ contact, onChanged }) {
  const [contactDoc, setContactDoc] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [tplKey, setTplKey] = useState("");
  const [lang, setLang] = useState("fr");
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({ status: "expresse", source: "", proof: "", date: "" });
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [copied, setCopied] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendChecked, setSendChecked] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const load = () => api.get(`/crm/contacts/${contact.id}`).then(({ data }) => setContactDoc(data)).catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail)));
  useEffect(() => { load(); api.get("/crm/email-templates").then(({ data }) => { setTemplates(data); if (data.length) setTplKey(data[0].key); }).catch(() => {}); }, [contact.id]);
  useEffect(() => {
    if (!tplKey) return;
    setPreview(null);
    setCopied(false);
    api.get("/crm/email-templates/preview", { params: { template_key: tplKey, contact_id: contact.id, lang } })
      .then(({ data }) => setPreview(data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail)));
  }, [tplKey, lang, contact.id, reloadKey]);

  const flash = (m) => { setNotice(m); setError(""); load(); onChanged(); setReloadKey((k) => k + 1); setTimeout(() => setNotice(""), 6000); };

  const saveConsent = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post(`/crm/contacts/${contact.id}/consent`, form);
      setShowForm(false);
      setForm({ status: "expresse", source: "", proof: "", date: "" });
      flash(form.status === "expresse" ? "Consentement expresse enregistré." : "Consentement implicite enregistré (24 mois).");
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const unsubscribe = async () => {
    const source = window.prompt("Source de la demande de désabonnement (ex. courriel reçu, appel du 14 oct.) :");
    if (!source || !source.trim()) return;
    setError("");
    try {
      await api.post(`/crm/contacts/${contact.id}/unsubscribe`, { source });
      flash("Désabonnement enregistré — tout envoi commercial est bloqué (LCAP).");
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const copyEmail = async () => {
    if (!preview?.allowed || !preview?.body) return;
    await navigator.clipboard.writeText(`Objet : ${preview.subject}\n\n${preview.body}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 4000);
  };

  const sendEmail = async () => {
    setError("");
    setSending(true);
    try {
      const { data } = await api.post(`/crm/contacts/${contact.id}/send-email`, {
        template_key: tplKey, lang, subject: preview.subject, body: preview.body, confirm: sendChecked,
      });
      setSendResult(data);
      if (data.ok) {
        setNotice(`Courriel envoyé à ${data.to}.`);
        setShowSendModal(false);
        setSendChecked(false);
        flash(`Courriel envoyé à ${data.to} — objet : ${preview.subject}`);
      }
    } catch (err) {
      setSendResult({ ok: false, error: formatApiErrorDetail(err.response?.data?.detail), to: contact.email });
    } finally {
      setSending(false);
    }
  };

  const c = contactDoc || contact;
  const status = c.consent_status || "";
  const allowed = Boolean(c.consent_allowed ?? contact.consent_allowed);

  return (
    <section className="rounded-2xl border border-line bg-white p-5" data-testid="crm-consent-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-steel">
          {allowed ? <ShieldCheck size={15} className="text-emerald-600" /> : status === "desabonne" ? <ShieldOff size={15} className="text-red-500" /> : <ShieldAlert size={15} className="text-amber-500" />}
          Consentement (LCAP)
        </h3>
        <span className={`rounded-full px-3 py-1 text-[11px] font-bold ${BADGES[status]}`} data-testid="crm-consent-badge">{CONSENT_LABELS[status]}</span>
      </div>

      <div className="mt-3 grid gap-1 text-xs text-graphite sm:grid-cols-2">
        <p>Source du contact : <span className="font-semibold">{c.consent_source || "—"}</span></p>
        <p>Date de collecte : <span className="font-semibold">{c.consent_date || "—"}</span></p>
        <p>Preuve du consentement : <span className="font-semibold">{c.consent_proof || "—"}</span></p>
        <p>Échéance (implicite) : <span className="font-semibold">{c.consent_expiry || "—"}</span></p>
        {c.unsubscribe_date && <p className="text-red-600">Désabonnement : {new Date(c.unsubscribe_date).toLocaleDateString("fr-CA")} ({c.unsubscribe_source || "—"})</p>}
      </div>

      {!allowed && <p className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-xs font-semibold text-red-700" data-testid="crm-consent-blocked">Envois commerciaux interdits — {c.consent_reason || "aucun consentement établi (LCAP)."}</p>}
      {allowed && <p className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-700" data-testid="crm-consent-ok">Envois commerciaux autorisés — {c.consent_reason || "consentement en vigueur."}</p>}

      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={() => setShowForm((s) => !s)} data-testid="crm-consent-toggle-form" className="rounded-full border border-line px-4 py-2 text-xs font-bold text-navy-800 hover:text-electric">
          {showForm ? "Annuler" : "Enregistrer un consentement"}
        </button>
        <button onClick={unsubscribe} data-testid="crm-consent-unsubscribe" className="rounded-full border border-red-200 px-4 py-2 text-xs font-bold text-red-600 hover:bg-red-50">
          Désabonnement
        </button>
      </div>

      {showForm && (
        <form onSubmit={saveConsent} className="mt-4 space-y-3 rounded-xl bg-paper p-4" data-testid="crm-consent-form">
          <div>
            <label htmlFor="cons-status" className="mb-1 block text-[12px] font-bold text-navy-900">Statut</label>
            <select id="cons-status" data-testid="crm-consent-status" value={form.status} onChange={(e) => setForm((s) => ({ ...s, status: e.target.value }))}
              className="w-full rounded-xl border border-line bg-white px-3 py-2 text-sm">
              <option value="expresse">Expresse (sans échéance)</option>
              <option value="implicite">Implicite (valide 24 mois)</option>
            </select>
          </div>
          <div>
            <label htmlFor="cons-source" className="mb-1 block text-[12px] font-bold text-navy-900">Source du contact / du consentement *</label>
            <input id="cons-source" required data-testid="crm-consent-source" value={form.source} onChange={(e) => setForm((s) => ({ ...s, source: e.target.value }))}
              placeholder="ex. formulaire web du 12 octobre, salon Industrielle" className="w-full rounded-xl border border-line bg-white px-3 py-2 text-sm" />
          </div>
          {form.status === "expresse" && (
            <div>
              <label htmlFor="cons-proof" className="mb-1 block text-[12px] font-bold text-navy-900">Preuve du consentement *</label>
              <input id="cons-proof" required data-testid="crm-consent-proof" value={form.proof} onChange={(e) => setForm((s) => ({ ...s, proof: e.target.value }))}
                placeholder="ex. capture du formulaire, courriel écrit de confirmation" className="w-full rounded-xl border border-line bg-white px-3 py-2 text-sm" />
            </div>
          )}
          <div>
            <label htmlFor="cons-date" className="mb-1 block text-[12px] font-bold text-navy-900">Date de collecte</label>
            <input id="cons-date" type="date" data-testid="crm-consent-date" value={form.date} onChange={(e) => setForm((s) => ({ ...s, date: e.target.value }))} className="rounded-xl border border-line bg-white px-3 py-2 text-sm" />
          </div>
          <button type="submit" data-testid="crm-consent-save" className="rounded-full bg-electric px-5 py-2 text-xs font-bold text-white hover:bg-electric-dark">Enregistrer</button>
        </form>
      )}

      <div className="mt-5 border-t border-line pt-4">
        <h4 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-steel"><Mail size={13} /> Préparer un courriel (envoi manuel — validation humaine)</h4>
        <div className="mt-3 flex flex-wrap gap-2">
          <select value={tplKey} onChange={(e) => setTplKey(e.target.value)} data-testid="crm-template-select" className="rounded-xl border border-line bg-white px-3 py-2 text-sm">
            {templates.map((t) => <option key={t.key} value={t.key}>{t.label_fr}</option>)}
          </select>
          <div className="flex rounded-xl border border-line bg-white p-0.5">
            {["fr", "en"].map((l) => (
              <button key={l} onClick={() => setLang(l)} data-testid={`crm-template-lang-${l}`}
                className={`rounded-lg px-3 py-1.5 text-xs font-bold uppercase ${lang === l ? "bg-navy-800 text-white" : "text-steel"}`}>{l}</button>
            ))}
          </div>
          <button onClick={copyEmail} disabled={!preview?.allowed} data-testid="crm-template-copy"
            className="inline-flex items-center gap-1.5 rounded-xl bg-navy-800 px-4 py-2 text-xs font-bold text-white disabled:cursor-not-allowed disabled:opacity-40">
            <Copy size={13} /> {copied ? "Copié !" : "Copier"}
          </button>
          <button onClick={() => { setSendResult(null); setSendChecked(false); setShowSendModal(true); }}
            disabled={!preview?.allowed || !(contactDoc?.email || contact.email)} data-testid="crm-email-send-button"
            className="inline-flex items-center gap-1.5 rounded-xl bg-electric px-4 py-2 text-xs font-bold text-white disabled:cursor-not-allowed disabled:opacity-40">
            <Mail size={13} /> Envoyer via Resend
          </button>
        </div>
        {preview?.allowed && !(contactDoc?.email || contact.email) && (
          <p className="mt-2 text-[11px] text-amber-700">Ce contact n'a aucune adresse courriel — ajout d'adresse requis pour l'envoi.</p>
        )}
        {preview && !preview.allowed && (
          <p className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-xs font-semibold text-red-700" data-testid="crm-template-blocked">
            Préparation bloquée — {preview.reason} Aucun contenu ne peut être copié pour ce contact.
          </p>
        )}
        {preview?.allowed && (
          <div className="mt-3 rounded-xl border border-line bg-paper p-4" data-testid="crm-template-preview">
            <p className="text-xs font-bold text-navy-900">Objet : {preview.subject}</p>
            <pre className="mt-2 whitespace-pre-wrap font-sans text-xs leading-relaxed text-graphite">{preview.body}</pre>
            <p className="mt-2 text-[11px] text-steel">Envoi réel possible via Resend après votre confirmation explicite (destinataire + contenu). Une copie reste recommandée pour vos dossiers.</p>
          </div>
        )}

        {(contactDoc?.emails || []).length > 0 && (
          <div className="mt-4" data-testid="crm-email-history">
            <p className="text-[11px] font-bold uppercase tracking-wider text-steel">Historique des envois ({contactDoc.emails.length})</p>
            <ul className="mt-2 space-y-1.5">
              {contactDoc.emails.slice(0, 5).map((m) => (
                <li key={m.id} className={`rounded-xl px-3 py-2 text-[11px] ${m.status === "sent" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                  {m.status === "sent" ? "✓" : "✗"} {m.to} — {m.subject} · {new Date(m.created_at).toLocaleString("fr-CA")} · par {m.sent_by}
                  {m.error && <span className="block">Erreur : {m.error}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {showSendModal && preview?.allowed && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-navy-950/50 p-4 backdrop-blur-sm" data-testid="crm-email-confirm-modal">
            <div className="w-full max-w-lg rounded-2xl bg-white p-7 shadow-card">
              <h3 className="font-display text-lg font-bold text-navy-950">Confirmer l'envoi du courriel</h3>
              <div className="mt-4 space-y-2 rounded-xl bg-paper p-4 text-sm">
                <p><span className="font-bold text-navy-900">Destinataire :</span> <span data-testid="crm-email-confirm-to">{contactDoc?.email || contact.email}</span></p>
                <p><span className="font-bold text-navy-900">Objet :</span> <span data-testid="crm-email-confirm-subject">{preview.subject}</span></p>
                <pre className="max-h-52 overflow-y-auto whitespace-pre-wrap rounded-xl border border-line bg-white p-3 font-sans text-xs text-graphite" data-testid="crm-email-confirm-body">{preview.body}</pre>
                <p className="text-[11px] text-steel">Modèle : {templates.find((t) => t.key === tplKey)?.label_fr || tplKey} ({lang.toUpperCase()}) · envoi via Resend — les réponses arrivent directement dans votre boîte de réception</p>
              </div>
              <label className="mt-4 flex cursor-pointer items-start gap-2 text-sm text-graphite">
                <input type="checkbox" checked={sendChecked} onChange={(e) => setSendChecked(e.target.checked)} data-testid="crm-email-confirm-checkbox" className="mt-0.5 h-4 w-4 accent-sky-600" />
                Je confirme le destinataire, le contenu et le consentement valide de ce contact, et j'autorise l'envoi.
              </label>
              {sendResult && !sendResult.ok && (
                <p role="alert" className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-700" data-testid="crm-email-result-error">Échec réel de l'envoi : {sendResult.error || "erreur inconnue"}</p>
              )}
              <div className="mt-5 flex justify-end gap-3">
                <button onClick={() => setShowSendModal(false)} data-testid="crm-email-confirm-cancel" className="rounded-full border border-line px-5 py-2.5 text-sm font-semibold text-graphite hover:text-navy-800">Annuler</button>
                <button onClick={sendEmail} disabled={!sendChecked || sending} data-testid="crm-email-confirm-submit"
                  className="rounded-full bg-electric px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-electric-dark disabled:opacity-50">
                  {sending ? "Envoi…" : "Confirmer et envoyer"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {notice && <p className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-700" data-testid="crm-consent-notice">{notice}</p>}
      {error && <p role="alert" className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-700" data-testid="crm-consent-error">{error}</p>}
    </section>
  );
}
